import type { FC, ReactNode } from 'react'
import type { Track } from '@/lib/tracks'

import { LoaderCircle, Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePlayer } from '@/components/player/player-provider'

interface PlayButtonProps {
    track: Track
    context?: Array<Track>
    size?: 'sm' | 'md' | 'lg'
    tone?: 'solid' | 'glass'
    className?: string
    label?: string
}

const sizes = {
    sm: 'size-9 [&_svg]:size-4',
    md: 'size-12 [&_svg]:size-5',
    lg: 'size-16 [&_svg]:size-6'
}

/** Plays a song through the persistent player, or pauses it when it is already playing. */
export const PlayButton: FC<PlayButtonProps> = ({ track, context, size = 'md', tone = 'solid', className, label }): ReactNode => {
    const { current, isPlaying, isBuffering, toggleTrack } = usePlayer()
    const active = current?.id === track.id
    const playing = active && isPlaying
    const loading = active && isBuffering && isPlaying

    return (
        <button
            type='button'
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                toggleTrack(track, context)
            }}
            aria-label={playing ? `Pause ${track.title}` : `${label ?? 'Play'} ${track.title}`}
            aria-pressed={playing}
            className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-full transition duration-300 ease-cinematic hover:scale-105 active:scale-95',
                tone === 'solid' ? 'bg-ivory text-obsidian hover:bg-gold' : 'border border-ivory/30 bg-obsidian/50 text-ivory backdrop-blur-md hover:border-ivory hover:bg-obsidian/70',
                sizes[size],
                className
            )}
        >
            {loading
                ? <LoaderCircle className='animate-spin' aria-hidden='true' />
                : playing
                    ? <Pause className='fill-current' aria-hidden='true' />
                    : <Play className='translate-x-[1px] fill-current' aria-hidden='true' />}
        </button>
    )
}

/** Three small bars that dance while a song is playing. */
export const PlayingBars: FC<{ className?: string, paused?: boolean }> = ({ className, paused = false }): ReactNode => (
    <span className={cn('inline-flex h-3 items-end gap-[2px]', className)} aria-hidden='true'>
        {[0, 1, 2].map(bar => (
            <span
                key={bar}
                className={cn('eq-bar w-[3px] rounded-full bg-gold', paused && '[animation-play-state:paused]')}
                style={{ height: '100%', animationDelay: `${bar * 0.18}s` }}
            />
        ))}
    </span>
)
